/**
 * Domain verification system constants and derived types.
 */

// ============================================================================
// Verification Methods & Statuses
// ============================================================================

export const VERIFICATION_METHODS = ["dns_txt", "html_file", "meta_tag"] as const;

export const VERIFICATION_STATUSES = ["verified", "failing", "unverified"] as const;

// ============================================================================
// Grace Period
// ============================================================================

/**
 * Number of days a domain can fail re-verification before being revoked.
 * During this grace period, users can fix their verification setup.
 */
export const VERIFICATION_GRACE_PERIOD_DAYS = 7;

// ============================================================================
// DNS Verification
// ============================================================================

/**
 * Prefix for DNS TXT record verification value.
 * Format: "domainstack-verification=TOKEN"
 */
export const DNS_VERIFICATION_PREFIX = "domainstack-verification=";

/**
 * Legacy DNS TXT record verification value prefix (backward compatibility).
 * Domains verified before the switch to DNS_VERIFICATION_PREFIX still carry
 * TXT records using this value and must keep matching on re-verification.
 */
export const DNS_VERIFICATION_PREFIX_LEGACY = "domainstack-verify=";

/**
 * Legacy subdomain prefix for DNS verification (backward compatibility).
 * Old format: _domainstack-verify.example.com TXT "domainstack-verify=TOKEN"
 * New format: example.com TXT "domainstack-verification=TOKEN"
 */
export const DNS_VERIFICATION_HOST_LEGACY = "_domainstack-verify";

/**
 * Recommended TTL for DNS verification records.
 * 1 minute allows for reasonable propagation testing without excessive caching.
 */
const DNS_VERIFICATION_TTL_MINUTES = 1;

/** Recommended TTL for DNS verification records, in seconds. */
export const DNS_VERIFICATION_TTL = DNS_VERIFICATION_TTL_MINUTES * 60;

/**
 * Human-readable label for the recommended TTL.
 */
export const DNS_VERIFICATION_TTL_LABEL = `${DNS_VERIFICATION_TTL_MINUTES} ${
  DNS_VERIFICATION_TTL_MINUTES === 1 ? "minute" : "minutes"
}`;

// ============================================================================
// HTML File Verification
// ============================================================================

/**
 * Directory path for HTML verification files.
 * Supports per-token files for multiple users: /.well-known/domainstack-verify/TOKEN.html
 */
export const HTML_FILE_DIR = "/.well-known/domainstack-verify";

/**
 * Legacy HTML verification file path (backward compatibility).
 * Original single-file approach: /.well-known/domainstack-verify.html
 */
export const HTML_FILE_PATH_LEGACY = "/.well-known/domainstack-verify.html";

/**
 * Prefix for HTML file content.
 * Format: "domainstack-verify: TOKEN"
 */
export const HTML_FILE_CONTENT_PREFIX = "domainstack-verify: ";

// ============================================================================
// Meta Tag Verification
// ============================================================================

/**
 * Name attribute for verification meta tag.
 * Format: <meta name="domainstack-verify" content="TOKEN">
 */
export const META_TAG_NAME = "domainstack-verify";
